declare function require(id: string): any;

type FontUrlMap = Record<string, string>;

const loadWebpackFontUrls = (): FontUrlMap | null => {
  try {
    return {
      russoOne: require("./fonts/RussoOne-Regular.ttf") as string,
      oswaldExtraLight: require("./fonts/Oswald-ExtraLight.ttf") as string,
      oswaldLight: require("./fonts/Oswald-Light.ttf") as string,
      oswaldRegular: require("./fonts/Oswald-Regular.ttf") as string,
      oswaldMedium: require("./fonts/Oswald-Medium.ttf") as string,
      oswaldSemiBold: require("./fonts/Oswald-SemiBold.ttf") as string,
      oswaldBold: require("./fonts/Oswald-Bold.ttf") as string,
    };
  } catch (error) {
    return null;
  }
};



const webpackFontUrls = loadWebpackFontUrls();

const fontSrc = (key: string, filename: string): string => {
  const webpackUrl = webpackFontUrls && webpackFontUrls[key];
  if (webpackUrl) {
    return webpackUrl;
  }

  // Relative URL only — avoid Node `path` / `file://` (breaks browser + live compile).
  return `./fonts/${filename}`;
};

const reportFontsCss = `
@font-face {
  font-family: "Russo One";
  font-style: normal;
  font-weight: 400;
  src: url("${fontSrc("russoOne", "RussoOne-Regular.ttf")}");
}

@font-face {
  font-family: "Oswald";
  font-style: normal;
  font-weight: 200;
  src: url("${fontSrc("oswaldExtraLight", "Oswald-ExtraLight.ttf")}");
}

@font-face {
  font-family: "Oswald";
  font-style: normal;
  font-weight: 300;
  src: url("${fontSrc("oswaldLight", "Oswald-Light.ttf")}");
}

@font-face {
  font-family: "Oswald";
  font-style: normal;
  font-weight: 400;
  src: url("${fontSrc("oswaldRegular", "Oswald-Regular.ttf")}");
}

@font-face {
  font-family: "Oswald";
  font-style: normal;
  font-weight: 500;
  src: url("${fontSrc("oswaldMedium", "Oswald-Medium.ttf")}");
}

@font-face {
  font-family: "Oswald";
  font-style: normal;
  font-weight: 600;
  src: url("${fontSrc("oswaldSemiBold", "Oswald-SemiBold.ttf")}");
}

@font-face {
  font-family: "Oswald";
  font-style: normal;
  font-weight: 700;
  src: url("${fontSrc("oswaldBold", "Oswald-Bold.ttf")}");
}
`;

const reportDesignCss: string = reportFontsCss + `
.coach-report-preview {
  --coach-report-accent: #79bde8;
  --coach-report-accent-rgb: 121, 189, 232;
  --coach-report-accent-tint: rgba(var(--coach-report-accent-rgb), 0.15);
  --coach-report-accent-soft: rgba(var(--coach-report-accent-rgb), 0.22);
  --coach-report-accent-muted: rgba(var(--coach-report-accent-rgb), 0.08);
  --coach-report-accent-wash: rgba(var(--coach-report-accent-rgb), 0.12);
  --green: #059669;
  box-sizing: border-box;
  color: #334155;
  font-family: "Open Sans", Arial, Helvetica, sans-serif;
  font-size: 8.5pt;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
  width: 100%;
}

.coach-report-preview__dynamic-score,
.coach-report-preview strong.coach-report-preview__dynamic-score,
.coach-report-preview__resource-why-chosen .coach-report-preview__dynamic-score {
  font-style: inherit;
  font-weight: 700;
}

.coach-report-preview *,
.coach-report-preview *::before,
.coach-report-preview *::after {
  box-sizing: border-box;
}

.coach-report-preview__toolbar {
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin: 0 0 12px;
  padding: 0 8px;
}

.coach-report-preview__save-status {
  color: #475569;
  font-size: 13px;
  font-weight: 600;
}

.coach-report-preview__toolbar-actions {
  align-items: center;
  display: flex;
  gap: 8px;
}

.coach-report-preview__save-button,
.coach-report-preview__print-button {
  background: var(--coach-report-accent);
  border: none;
  border-radius: 5px;
  color: #ffffff;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.2;
  padding: 8px 10px;
}

.coach-report-preview__print-button {
  font-size: 13px;
  padding: 8px 10px;
}

.coach-report-preview__save-button:hover:not(:disabled),
.coach-report-preview__print-button:hover:not(:disabled) {
  filter: brightness(0.92);
}

.coach-report-preview__save-button:disabled,
.coach-report-preview__print-button:disabled {
  background: #cbd5e1;
  color: #64748b;
  cursor: not-allowed;
  filter: none;
}

.coach-report-preview__page {
  background: #ffffff;
  box-sizing: border-box;
  max-width: 100%;
  overflow-x: hidden;
  overflow-y: hidden;
  position: relative;
  width: 100%;
}

.coach-report-preview__page--fill {
  display: flex;
  flex-direction: column;
  min-height: 1056px;
}

.coach-report-preview__page-content--fill {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  width: 100%;
}

.coach-report-preview__report-header {
  border-bottom: 3px solid var(--coach-report-accent);
  max-width: 100%;
  min-width: 0;
  padding: 18px 44px 0;
}

.coach-report-preview__header-top {
  align-items: flex-start;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 10px;
  min-width: 0;
}

.coach-report-preview__header-top > div:first-child {
  flex: 1 1 auto;
  min-width: 0;
}

.coach-report-preview__school-name {
  color: #1a2a3a;
  font-size: 21pt;
  font-weight: 900;
  letter-spacing: -0.5px;
  line-height: 1.1;
  overflow-wrap: break-word;
  word-break: break-word;
}

.coach-report-preview__report-subtitle {
  color: #334155;
  font-size: 10.5pt;
  font-weight: 700;
  letter-spacing: 0.3px;
  line-height: 1.2;
  margin-top: 3px;
  text-transform: none;
}

.coach-report-preview__school-logo {
  align-items: center;
  border-radius: 10px;
  color: #1a2a3a;
  display: flex;
  flex-shrink: 0;
  font-size: 18px;
  font-weight: 900;
  height: 52px;
  justify-content: center;
  overflow: hidden;
  width: 52px;
}

.coach-report-preview__school-logo-image {
  display: block;
  height: 100%;
  object-fit: contain;
  width: 100%;
}

.coach-report-preview__header-meta {
  border-top: 1px solid #e2e8f0;
  display: flex;
  flex-wrap: wrap;
  gap: 16px 32px;
  min-width: 0;
  padding: 8px 0 10px;
}

.coach-report-preview__meta-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 100%;
  min-width: 0;
}

.coach-report-preview__meta-label {
  color: #64748b;
  font-size: 6.5pt;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.coach-report-preview__meta-value {
  color: #1a2a3a;
  font-size: 9.5pt;
  font-weight: 700;
  overflow-wrap: break-word;
  word-break: break-word;
}

.coach-report-preview__running-header,
.coach-report-preview__data-section-banner {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 8px 44px;
}

.coach-report-preview__running-header {
  background: var(--coach-report-accent);
  color: #ffffff;
}

.coach-report-preview__running-header-left {
  color: rgba(255, 255, 255, 0.8);
  font-size: 8pt;
  font-weight: 600;
}

.coach-report-preview__running-header-right {
  color: rgba(255, 255, 255, 0.45);
  font-size: 7.5pt;
}

.coach-report-preview__data-section-banner {
  background: linear-gradient(135deg, #1a2a3a 0%, #2a4a6a 100%);
  color: #ffffff;
  display: block;
}

.coach-report-preview__data-section-banner-title {
  font-size: 12pt;
  font-weight: 800;
}

.coach-report-preview__data-section-banner-sub {
  color: rgba(255, 255, 255, 0.6);
  font-size: 8pt;
  margin-top: 4px;
}

.coach-report-preview__page-content {
  max-width: 100%;
  min-width: 0;
  padding: 18px 44px 55px;
  width: 100%;
}

.coach-report-preview__page-footer {
  align-items: center;
  border-top: 1px solid #e2e8f0;
  bottom: 0;
  display: flex;
  justify-content: space-between;
  left: 0;
  padding: 7px 44px;
  position: absolute;
  right: 0;
}

.coach-report-preview__footer-brand,
.coach-report-preview__footer-page {
  color: #64748b;
  font-size: 7pt;
}

.coach-report-preview__footer-page {
  font-weight: 600;
}

.coach-report-preview__section-header {
  align-items: center;
  display: flex;
  gap: 9px;
  margin: 16px 0 9px;
}

.coach-report-preview__section-bar {
  align-self: stretch;
  background: var(--coach-report-accent);
  border-radius: 2px;
  min-height: 22px;
  width: 4px;
}

.coach-report-preview__section-title {
  color: #1a2a3a;
  font-size: 13pt;
  font-weight: 800;
  letter-spacing: -0.2px;
}

.coach-report-preview__kpi-grid {
  display: grid;
  gap: 9px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  margin-bottom: 16px;
  margin-top: 10px;
}

.coach-report-preview__kpi-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  border-top: 4px solid var(--coach-report-accent);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  overflow: hidden;
  padding: 8px 10px;
  position: relative;
  text-align: center;
}

.coach-report-preview__kpi-value {
  color: #1a2a3a;
  font-size: 15pt;
  font-weight: 800;
  hyphens: none;
  line-height: 1;
  margin-bottom: 2px;
  max-width: 100%;
  overflow-wrap: break-word;
  word-break: break-word;
}

.coach-report-preview__kpi-value--long {
  font-size: 9.5pt;
  letter-spacing: -0.02em;
  line-height: 1.2;
  padding-top: 4px;
}

.coach-report-preview__kpi-label {
  color: #64748b;
  font-size: 7pt;
  font-weight: 500;
  letter-spacing: 0.4px;
  line-height: 1.3;
  max-width: 100%;
  overflow-wrap: break-word;
  text-transform: uppercase;
  word-break: break-word;
}

.coach-report-preview__highlight-grid {
  align-items: stretch;
  display: grid;
  gap: 9px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 10px;
  max-width: 100%;
  min-width: 0;
}

.coach-report-preview__resource-grid {
  align-items: stretch;
  display: grid;
  gap: 9px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-bottom: 10px;
  max-width: 100%;
  min-width: 0;
}

.coach-report-preview__connectedness-block {
  margin-top: 12px;
  max-width: 100%;
  min-width: 0;
}

.coach-report-preview__connectedness-block .coach-report-preview__section-header {
  margin-top: 0;
  margin-bottom: 12px;
}

.coach-report-preview__connectedness-block .coach-report-preview__data-table {
  margin-bottom: 0;
}

.coach-report-preview__connectedness-footer-note {
  border-radius: 8px;
  box-sizing: border-box;
  color: #334155;
  font-size: 12px;
  height: auto;
  line-height: 1.5;
  margin: 10px 0 0;
  max-width: 100%;
  min-height: 0;
  white-space: normal;
  width: 100%;
  word-break: normal;
  overflow-wrap: break-word;
}

.coach-report-preview__resource-card,
.coach-report-preview__overall-summary-box,
.coach-report-preview__final-note-box,
.coach-report-preview__leader-panel,
.coach-report-preview__admin-panel {
  border-radius: 9px;
}
.coach-report-preview__summary-box{
    border-radius: 9px;
}
.coach-report-preview__highlight-card{
    border-radius: 12px;
}

.coach-report-preview__overall-summary-box {
  border: none;
  margin-bottom: 14px;
  padding: 4px 2px;
}

.coach-report-preview__highlight-card {
  align-self: stretch;
  background: var(--coach-report-accent-tint);
  border: 1px solid #bae6fd;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: auto;
  min-height: 0;
  min-width: 0;
  padding: 8px 11px;
}

.coach-report-preview__highlight-num,
.coach-report-preview__highlight-num-input {
  color: var(--coach-report-accent);
  flex: 0 0 auto;
  font-size: 7pt;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.coach-report-preview__highlight-num-input,
.coach-report-preview__highlight-title-input {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  font-family: inherit;
  margin: 0;
  outline: none;
  padding: 2px 4px;
  width: 100%;
}

.coach-report-preview__highlight-num-input:focus,
.coach-report-preview__highlight-title-input:focus {
  /* Match reportt.html [contenteditable]:focus background */
  background: rgba(100, 160, 255, 0.1);
  border-color: transparent;
  box-shadow: none;
  outline: none;
}

.coach-report-preview__highlight-title,
.coach-report-preview__highlight-title-input,
.coach-report-preview__suggestion-title,
.coach-report-preview__resource-title,
.coach-report-preview__leader-quadrant,
.coach-report-preview__comment-title,
.coach-report-preview__final-note-title {
  color: #1a2a3a;
  font-weight: 700;
}

.coach-report-preview__highlight-title,
.coach-report-preview__highlight-title-input {
  box-sizing: border-box;
  flex: 0 0 auto;
  font-size: 8.5pt;
  line-height: 1.3;
  margin-bottom: 4px;
}

.coach-report-preview__suggestion-title {
  font-size: 8.5pt;
  line-height: 1.3;
  margin-bottom: 4px;
}

.coach-report-preview__resource-title {
  font-size: 9pt;
  line-height: 1.35;
  margin-bottom: 2px;
}

.coach-report-preview__highlight-body {
  color: #334155;
  flex: 1;
  font-size: 7pt;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}

.coach-report-preview__highlight-card .coach-report-preview__editable-field-wrap {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  /* Keep a hard gap so body text never sits on / under the counter. */
  gap: 8px;
  margin-bottom: 0;
  min-height: 0;
  overflow: visible;
  width: 100%;
}

.coach-report-preview__highlight-card .coach-report-preview__editable-field-surface {
  display: block;
  flex: 0 0 auto;
  min-height: 0;
  /* Clip absolute preview text to the sized textarea box — no card spill. */
  overflow: hidden;
  width: 100%;
}

.coach-report-preview__highlight-card
  .coach-report-preview__editable-field-wrap
  .coach-report-preview__edit-field,
.coach-report-preview__highlight-card .coach-report-preview__editable-text--highlight {
  box-sizing: border-box;
  flex: 0 0 auto;
  min-height: 0;
  width: 100%;
}

/* Pin letter counters below the body with a fixed gap (never overlapping text). */
.coach-report-preview__highlight-card .coach-report-preview__char-limit {
  align-self: flex-end;
  flex: 0 0 auto;
  margin-top: auto;
  position: relative;
  z-index: 2;
}

.coach-report-preview__data-table,
.coach-report-preview__comparison-table {
  border-collapse: collapse;
  margin-bottom: 12px;
  overflow: hidden;
  table-layout: fixed;
  width: 100%;
}

.coach-report-preview__data-table {
  border-radius: 8px;
}

.coach-report-preview__data-table-wrap {
  border-radius: 8px;
  margin-bottom: 10px;
  max-width: 100%;
  overflow: hidden;
  width: 100%;
}

.coach-report-preview__data-table-wrap .coach-report-preview__data-table {
  margin-bottom: 0;
}

.coach-report-preview__data-table-wrap .coach-report-preview__data-table td {
  border-bottom: none;
}

.coach-report-preview__data-table-wrap .coach-report-preview__data-table tbody tr:nth-child(odd) {
  background: #ffffff;
}

.coach-report-preview__data-table-wrap .coach-report-preview__data-table tbody tr:nth-child(even) {
  background: #f8fafc;
}

.coach-report-preview__comparison-table-wrap {
  border-radius: 8px;
  margin-bottom: 12px;
  overflow: hidden;
}

.coach-report-preview__comparison-table {
  margin-bottom: 0;
}

/* Round dark header / average bars (single + paired tables). */
.coach-report-preview__comparison-table thead tr:first-child th:first-child,
.coach-report-preview__data-table thead tr:first-child th:first-child {
  border-top-left-radius: 8px;
}

.coach-report-preview__comparison-table:not(.coach-report-preview__comparison-table--pair)
  thead
  tr:first-child
  th:last-child,
.coach-report-preview__data-table thead tr:first-child th:last-child {
  border-top-right-radius: 8px;
}

.coach-report-preview__comparison-table tfoot tr:last-child td:first-child,
.coach-report-preview__data-table tfoot tr:last-child td:first-child {
  border-bottom-left-radius: 8px;
}

.coach-report-preview__comparison-table:not(.coach-report-preview__comparison-table--pair)
  tfoot
  tr:last-child
  td:last-child,
.coach-report-preview__data-table tfoot tr:last-child td:last-child {
  border-bottom-right-radius: 8px;
}

/* Paired Expectations | Relational Warmth — round each side like two cards. */
.coach-report-preview__comparison-table--pair thead tr:first-child th:nth-child(3) {
  border-top-right-radius: 8px;
}

.coach-report-preview__comparison-table--pair thead tr:first-child th:nth-child(5) {
  border-top-left-radius: 8px;
}

.coach-report-preview__comparison-table--pair thead tr:first-child th:last-child {
  border-top-right-radius: 8px;
}

.coach-report-preview__comparison-table--pair tfoot tr:last-child td:nth-child(3) {
  border-bottom-right-radius: 8px;
}

.coach-report-preview__comparison-table--pair tfoot tr:last-child td:nth-child(5) {
  border-bottom-left-radius: 8px;
}

.coach-report-preview__comparison-table--pair tfoot tr:last-child td:last-child {
  border-bottom-right-radius: 8px;
}

.coach-report-preview__data-table th,
.coach-report-preview__data-table td {
  border: none;
  border-bottom: 1px solid #e2e8f0;
  font-size: 7.5pt;
  padding: 7px 11px;
  vertical-align: middle;
}

.coach-report-preview__data-table tbody td {
  padding: 5px 11px;
}

.coach-report-preview__comparison-table th,
.coach-report-preview__comparison-table td {
  border: none;
  border-bottom: 1px solid #e2e8f0;
  font-size: 6.8pt;
  padding: 4px 8px;
  vertical-align: middle;
}

.coach-report-preview__comparison-table thead th {
  padding: 7px 10px;
}

.coach-report-preview__comparison-table tbody tr {
  height: 22px;
}

.coach-report-preview__data-table thead th {
  background: #1a2a3a;
  border-bottom: none;
  color: #ffffff;
  font-size: 7.5pt;
  font-weight: 600;
}

.coach-report-preview__data-table tfoot td,
.coach-report-preview__data-table-summary td,
.coach-report-preview__connectedness-total-row td {
  background: #1a2a3a;
  border-bottom: none;
  color: #ffffff;
  font-size: 7.5pt;
  font-weight: 700;
}

.coach-report-preview__data-table thead th:first-child,
.coach-report-preview__data-table tbody td:first-child,
.coach-report-preview__data-table tfoot td:first-child {
  text-align: left;
}

.coach-report-preview__comparison-table thead th {
  background: #1a2a3a;
  border-bottom: none;
  color: #ffffff;
  font-size: 7pt;
  font-weight: 600;
}

.coach-report-preview__comparison-table tfoot td {
  background: #1a2a3a;
  border-bottom: none;
  color: #ffffff;
  font-size: 7pt;
  font-weight: 700;
}

.coach-report-preview__comparison-table thead th:nth-child(2),
.coach-report-preview__comparison-table thead th:nth-child(3),
.coach-report-preview__comparison-table td.coach-report-preview__score-col,
.coach-report-preview__comparison-table tfoot td.coach-report-preview__score-col {
  white-space: nowrap;
  width: 58px;
}

.coach-report-preview__comparison-table thead th:nth-child(2),
.coach-report-preview__comparison-table thead th:nth-child(3),
.coach-report-preview__comparison-table td.coach-report-preview__score-col,
.coach-report-preview__comparison-table tfoot td.coach-report-preview__score-col {
  text-align: center;
}

.coach-report-preview__comparison-table td.coach-report-preview__score-col {
  font-weight: 600;
}

.coach-report-preview__comparison-table tbody tr:nth-child(odd) {
  background: #ffffff;
}

.coach-report-preview__comparison-table tbody tr:nth-child(even) {
  background: #f8fafc;
}

.coach-report-preview__score-legend {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-bottom: 8px;
}

.coach-report-preview__score-legend-item {
  align-items: center;
  color: #334155;
  display: inline-flex;
  font-size: 12px;
  font-weight: 600;
  gap: 8px;
}

.coach-report-preview__score-legend-swatch {
  border-radius: 15px;
  display: inline-block;
  height: 7px;
  width: 7px;
}

.coach-report-preview__score-legend-swatch--student {
  background: var(--coach-report-accent);
}

.coach-report-preview__score-legend-swatch--coach {
  background: #1a2a3a;
}

.coach-report-preview__comparison-label-input {
  color: #334155;
  font-size: 6.8pt;
  line-height: 1.45;
  min-height: 20px;
  outline: none;
  white-space: pre-wrap;
  word-break: break-word;
}

.coach-report-preview__comparison-score {
  font-size: 7pt;
  font-weight: 700;
  min-height: 20px;
  outline: none;
  text-align: center;
  white-space: nowrap;
}

.coach-report-preview__comparison-score--student {
  color: var(--coach-report-accent);
}

.coach-report-preview__comparison-score--low {
  color: #b45309 !important;
}

.coach-report-preview__comparison-score--coach {
  color: #1a2a3a;
}

.coach-report-preview__comparison-footer-label,
.coach-report-preview__comparison-footer-score {
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  min-height: 18px;
  outline: none;
  white-space: pre-wrap;
}

.coach-report-preview__comparison-footer-score {
  text-align: center;
}

.coach-report-preview__comparison-label-input:focus,
.coach-report-preview__comparison-score:focus,
.coach-report-preview__comparison-summary-body:focus,
.coach-report-preview__comparison-footer-label:focus,
.coach-report-preview__comparison-footer-score:focus {
  /* Match reportt.html [contenteditable]:focus */
  background: rgba(100, 160, 255, 0.1);
  border-color: transparent;
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(100, 160, 255, 0.25);
  outline: none;
}

.coach-report-preview__comparison-summary-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 14px 16px;
}

.coach-report-preview__comparison-summary-label {
  color: #1a2a3a;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.coach-report-preview__comparison-summary-body {
  color: #334155;
  font-size: 13px;
  line-height: 1.55;
  min-height: 72px;
  outline: none;
  white-space: pre-wrap;
  word-break: break-word;
}

.coach-report-preview__data-table thead th:last-child,
.coach-report-preview__data-table td:last-child,
.coach-report-preview__data-table tfoot td:last-child,
.coach-report-preview__data-table-summary td:last-child {
  text-align: right;
  width: 170px;
}

.coach-report-preview__data-table tbody td:last-child {
  font-weight: 600;
}

.coach-report-preview__data-table tbody tr:nth-child(odd) {
  background: #ffffff;
}

.coach-report-preview__data-table tbody tr:nth-child(even) {
  background: #f8fafc;
}

.coach-report-preview__score-badge {
  background: var(--coach-report-accent);
  border-radius: 999px;
  color: #ffffff;
  display: inline-flex;
  font-size: 7.5pt;
  font-weight: 700;
  justify-content: center;
  min-width: 58px;
  padding: 4px 10px;
}

.coach-report-preview__comp-badge {
  background: var(--coach-report-accent);
  border-radius: 999px;
  color: #ffffff;
  display: inline-flex;
  font-size: 7pt;
  font-weight: 700;
  justify-content: center;
  min-width: 58px;
  padding: 4px 10px;
}

.coach-report-preview__score-badge--dark,
.coach-report-preview__comp-badge--dark {
  background: #1a2a3a;
}

.coach-report-preview__comp-badge--light {
  background: var(--coach-report-accent-soft);
  color: #1a2a3a;
}

.coach-report-preview__comp-score {
  font-size: 7pt;
  font-weight: 700;
}

.coach-report-preview__comp-score--student {
  color: var(--coach-report-accent);
}

.coach-report-preview__comp-score--coach {
  color: #1a2a3a;
}

.coach-report-preview__comp-score--low {
  color: #b45309;
}

/* Comparison score + mini progress bar (fixed track width, dynamic fill). */
.coach-report-preview__score-meter {
  align-items: center;
  display: inline-flex;
  gap: 15px;
  justify-content: flex-end;
  max-width: 100%;
  white-space: nowrap;
}

.coach-report-preview__comparison-table .coach-report-preview__score-meter {
  display: flex;
  justify-content: center;
  width: 100%;
}

.coach-report-preview__comparison-table .coach-report-preview__score-meter-value {
  text-align: center;
}

.coach-report-preview__score-meter-value {
  font-size: 7pt;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1;
  min-width: 2.1em;
  text-align: right;
}

.coach-report-preview__score-meter-track {
  background: #e2e8f0;
  border-radius: 999px;
  display: inline-block;
  flex: 0 0 15px;
  height: 6px;
  overflow: hidden;
  position: relative;
  width: 22px;
}

.coach-report-preview__score-meter-fill {
  border-radius: 999px;
  display: block;
  height: 100%;
  max-width: 100%;
  min-width: 0;
}

.coach-report-preview__score-meter--mid .coach-report-preview__score-meter-value,
.coach-report-preview__score-meter--high .coach-report-preview__score-meter-value {
  color: var(--coach-report-accent);
}

.coach-report-preview__score-meter--mid .coach-report-preview__score-meter-track,
.coach-report-preview__score-meter--high .coach-report-preview__score-meter-track {
  background: var(--coach-report-accent-tint);
}

.coach-report-preview__score-meter--mid .coach-report-preview__score-meter-fill,
.coach-report-preview__score-meter--high .coach-report-preview__score-meter-fill {
  background: var(--coach-report-accent);
}

.coach-report-preview__score-meter--low .coach-report-preview__score-meter-value {
  color: #b45309;
}

.coach-report-preview__score-meter--low .coach-report-preview__score-meter-track {
  background: rgba(180, 83, 9, 0.22);
}

.coach-report-preview__score-meter--low .coach-report-preview__score-meter-fill {
  background: #b45309;
}

.coach-report-preview__score-meter--coach .coach-report-preview__score-meter-value {
  color: #000000;
}

.coach-report-preview__score-meter--coach .coach-report-preview__score-meter-track {
  background: rgba(0, 0, 0, 0.18);
}

.coach-report-preview__score-meter--coach .coach-report-preview__score-meter-fill {
  background: #000000;
}

.coach-report-preview__summary-box .coach-report-preview__editable-text {
  border: none;
  min-height: 0;
  padding: 6;
}

.coach-report-preview__summary-box .coach-report-preview__editable-text:focus {
  /* Match reportt.html [contenteditable]:focus */
  background: rgba(100, 160, 255, 0.1);
  border-color: transparent;
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(100, 160, 255, 0.25);
  outline: none;
}

.coach-report-preview__connectedness-score-text {
  color: #1a2a3a;
  font-size: 7.5pt;
  font-weight: 700;
  min-width: 34px;
  text-align: center;
  white-space: nowrap;
}

.coach-report-preview__connectedness-score-text--message {
  font-size: 11px;
  font-weight: 600;
  line-height: 1.35;
  max-width: none;
  min-width: 0;
  padding-right: 0;
  text-align: center;
  white-space: nowrap;
}

.coach-report-preview__data-table tfoot .coach-report-preview__connectedness-score-text,
.coach-report-preview__data-table-summary .coach-report-preview__connectedness-score-text,
.coach-report-preview__connectedness-total-row .coach-report-preview__connectedness-score-text {
  color: #ffffff;
}

.coach-report-preview__score-bar-wrap {
  background: #e2e8f0;
  border-radius: 3px;
  flex: 0 0 40px;
  height: 5px;
  overflow: hidden;
  width: 40px;
}

.coach-report-preview__score-bar {
  background: var(--coach-report-accent);
  border-radius: 3px;
  display: block;
  height: 100%;
  max-width: 100%;
  min-width: 0;
}

.coach-report-preview__data-table-wrap .coach-report-preview__score-bar-wrap {
  background: #e2e8f0;
}

.coach-report-preview__data-table-wrap .coach-report-preview__score-bar {
  background: var(--coach-report-accent);
}

.coach-report-preview__data-table--parent-feedback .coach-report-preview__score-bar,
.coach-report-preview__data-table-wrap--parent-feedback .coach-report-preview__score-bar {
  background: linear-gradient(90deg, var(--green) 0%, #34d399 100%);
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.coach-report-preview__data-table th.coach-report-preview__score-heading,
.coach-report-preview__data-table td.coach-report-preview__score-col {
  text-align: center;
  vertical-align: middle;
  width: 100px;
}

.coach-report-preview__connectedness-block .coach-report-preview__data-table th.coach-report-preview__score-heading,
.coach-report-preview__connectedness-block .coach-report-preview__data-table td.coach-report-preview__score-col {
  width: 200px;
}

.coach-report-preview__data-table td.coach-report-preview__score-col {
  white-space: nowrap;
}

.coach-report-preview__score-cell {
  align-items: center;
  display: inline-flex;
  gap: 5px;
  justify-content: center;
  max-width: 100%;
  min-width: 0;
  width: 100%;
}

.coach-report-preview__score-cell--message {
  align-items: center;
  display: flex;
  justify-content: center;
  width: 100%;
}

.coach-report-preview__score-col {
  vertical-align: middle;
  white-space: nowrap;
  width: 108px;
}

.coach-report-preview__summary-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #1a2a3a;
  padding: 12px 16px;
}

.coach-report-preview__final-note-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-left: 4px solid #1a2a3a;
  margin-bottom: 12px;
  padding: 14px 18px;
}

.coach-report-preview__data-table-wrap + .coach-report-preview__summary-box {
  margin-top: 14px;
}

.coach-report-preview__summary-box-label {
  color: #1a2a3a;
  font-size: 6.5pt;
  font-weight: 700;
  letter-spacing: 0.6px;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.coach-report-preview__admin-label {
  color: #1a2a3a;
  font-size: 6.5pt;
  font-weight: 700;
  letter-spacing: 0.6px;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.coach-report-preview__summary-box-text,
.coach-report-preview__intro-text,
.coach-report-preview__leader-summary,
.coach-report-preview__resource-body {
  color: #334155;
  font-size: 8.5pt;
  line-height: 1.6;
}

.coach-report-preview__comment-body {
  color: #334155;
  font-size: 7.5pt;
  line-height: 1.6;
}

.coach-report-preview__leader-commitments-intro {
  color: #64748b;
  font-size: 8pt;
  line-height: 1.6;
  margin-bottom: 10px;
}

.coach-report-preview__suggestion-list {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin-bottom: 10px;
  padding-left: 0;
  position: relative;
}


.coach-report-preview__suggestion-item {
  align-items: flex-start;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-left: 4px solid var(--coach-report-accent);
  border-radius: 7px;
  box-shadow: none;
  display: flex;
  gap: 10px;
  overflow: hidden;
  padding: 9px 11px;
  position: relative;
  z-index: 1;
}

.coach-report-preview__suggestion-item::before {
  content: none;
  display: none;
}

.coach-report-preview__suggestion-icon {
  align-items: center;
  background: var(--coach-report-accent);
  border-radius: 5px;
  color: #ffffff;
  display: flex;
  flex-shrink: 0;
  font-size: 8pt;
  font-weight: 800;
  height: 22px;
  justify-content: center;
  margin-left: 0;
  margin-top: 0;
  position: relative;
  width: 22px;
  z-index: 2;
}

.coach-report-preview__suggestion-content {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0px;
  min-width: 0;
}

.pull-quote {
    background: linear-gradient(135deg, #1a2a3a 0%, #1e3a5f 100%) !important;
    color: white;
    border-radius: 9px;
    padding: 12px 18px;
    margin: 10px 0;
    page-break-inside: avoid;
}
.pull-quote-text {
    font-size: 9.5pt;
    font-style: italic;
    font-weight: 500;
    line-height: 1.5;
    padding-left: 16px;
    position: relative;
}
.pull-quote-text::before {
    content: '"';
    position: absolute;
    left: 0;
    top: -4px;
    font-size: 22pt;
    color: rgba(255, 255, 255, 0.2);
    font-family: Georgia, serif;
    line-height: 1;
}
.pull-quote-attr {
    font-size: 7.5pt;
    color: rgba(255, 255, 255, 0.55);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 6px;
    padding-left: 16px;
}
.coach-report-preview__suggestion-title-input {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  color: #1a2a3a;
  font-family: inherit;
  font-size: 8.5pt;
  font-weight: 700;
  line-height: 1.35;
  margin: 0;
  min-height: 24px;
  outline: none;
  padding: 2px 4px;
  white-space: pre-wrap;
  width: 100%;
  word-break: break-word;
}

.coach-report-preview__suggestion-title-input:focus {
  /* Match reportt.html [contenteditable]:focus */
  background: rgba(100, 160, 255, 0.1);
  border-color: transparent;
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(100, 160, 255, 0.25);
  outline: none;
}

.coach-report-preview__suggestion-item .coach-report-preview__editable-text--suggestion {
  color: #334155;
  font-size: 7.5pt;
  height: auto;
  line-height: 1.55;
  max-height: none;
  min-height: 0;
  overflow: hidden;
  resize: none;
}

.coach-report-preview__suggestion-item .coach-report-preview__editable-text--suggestion:focus {
  /* Match reportt.html [contenteditable]:focus */
  background: rgba(100, 160, 255, 0.1);
  border-color: transparent;
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(100, 160, 255, 0.25);
  outline: none;
}

.coach-report-preview__suggestion-quote {
  background: #1a2a3a;
  border-radius: 12px;
  color: #ffffff;
  margin-top: 16px;
  padding: 26px 28px 22px;
  position: relative;
  text-align: center;
}

.coach-report-preview__suggestion-quote::before {
  color: rgba(255, 255, 255, 0.2);
  content: "\\201C";
  font-family: Georgia, "Times New Roman", serif;
  font-size: 22pt;
  left: 22px;
  line-height: 1;
  position: absolute;
  top: 12px;
}

.coach-report-preview__resource-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  padding: 12px;
}

.coach-report-preview__pull-quote-text {
  color: #ffffff;
  font-size: 9.5pt;
  font-style: italic;
  font-weight: 500;
  line-height: 1.55;
  margin: 0 auto;
  max-width: 640px;
  min-height: 28px;
  outline: none;
  padding: 4px 8px;
  white-space: pre-wrap;
  word-break: break-word;
}

.coach-report-preview__pull-quote-text:focus,
.coach-report-preview__pull-quote-attr:focus {
  /* Match reportt.html [contenteditable]:focus */
  background: rgba(100, 160, 255, 0.1);
  border-color: transparent;
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(100, 160, 255, 0.25);
  outline: none;
}

.coach-report-preview__pull-quote-attr {
  color: rgba(255, 255, 255, 0.55);
  font-size: 7.5pt;
  font-style: normal;
  font-weight: 600;
  letter-spacing: 0.1em;
  margin: 10px auto 0;
  max-width: 640px;
  min-height: 20px;
  outline: none;
  padding: 2px 8px;
  text-transform: uppercase;
  white-space: pre-wrap;
}

.coach-report-preview__resource-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 10px;
  margin-top: 4px;
}

.coach-report-preview__resource-card-big {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-left: 4px solid var(--coach-report-accent);
  border-radius: 10px;
  box-shadow: none;
  box-sizing: border-box;
  overflow: hidden;
  padding: 14px 16px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.coach-report-preview__resource-card-big--book {
  border-left-color: #1a3a5c;
}

.coach-report-preview__resource-card-big--article {
  border-left-color: #0d7a6e;
}

.coach-report-preview__resource-card-big--podcast {
  border-left-color: #b45309;
}

.coach-report-preview__resource-type-badge {
  border-radius: 20px;
  display: inline-block;
  font-size: 6.5pt;
  font-weight: 700;
  letter-spacing: 0.8px;
  line-height: 1.2;
  margin-bottom: 6px;
  padding: 2px 8px;
  text-transform: uppercase;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.coach-report-preview__resource-card-top {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 0;
  width: 100%;
}

.coach-report-preview__resource-card-actions {
  display: flex;
  justify-content: flex-end;
  margin: 0;
}

.coach-report-preview__resource-card-top
  > .coach-report-preview__resource-type-badge:only-child {
  margin-right: auto;
}

.coach-report-preview__resource-card-big--book .coach-report-preview__resource-type-badge {
  background: #dce8f5;
  color: #1a3a5c;
}

.coach-report-preview__resource-card-big--article .coach-report-preview__resource-type-badge {
  background: #d0f0ec;
  color: #0d7a6e;
}

.coach-report-preview__resource-card-big--podcast .coach-report-preview__resource-type-badge {
  background: #fef3c7;
  color: #b45309;
}

.coach-report-preview__resource-title-big,
.coach-report-preview__editable-text--resource-title {
  color: #1a2a3a;
  font-size: 9pt;
  font-weight: 700;
  line-height: 1.3;
  margin: 0 0 5px;
}

.coach-report-preview__resource-desc {
  color: #334155;
  font-size: 7.5pt;
  line-height: 1.5;
  margin: 0 0 5px;
}

.coach-report-preview__resource-author {
  color: var(--coach-report-accent);
  font-size: 11px;
  font-weight: 700;
  margin: 4px 0 8px;
}

.coach-report-preview__resource-why-chosen {
  border-top: 1px solid #e2e8f0;
  color: #64748b;
  font-size: 7.5pt;
  font-style: italic;
  line-height: 1.5;
  margin-top: 4px;
  padding-top: 5px;
}

.coach-report-preview__resource-why-chosen-para {
  margin: 0;
  padding: 0;
}

.coach-report-preview__resource-why-chosen-label,
.coach-report-preview__resource-why-chosen strong {
  color: #1a2a3a;
  display: inline;
  float: none;
  font-style: normal;
  font-weight: 700;
  margin: 0;
}

.coach-report-preview__resource-why-chosen-text,
.coach-report-preview__resource-why-chosen
  .coach-report-preview__editable-text--resource-why-chosen.coach-report-preview__edit-field,
.coach-report-preview__resource-why-chosen
  .coach-report-preview__editable-text--resource-why-chosen.coach-report-preview__print-text {
  color: #64748b;
  display: inline;
  font-style: italic;
  font-weight: 400;
  white-space: pre-wrap;
  word-break: break-word;
}

.coach-report-preview__resource-why-chosen
  .coach-report-preview__editable-text--resource-why-chosen.coach-report-preview__edit-field {
  background: transparent;
  border: none;
  box-shadow: none;
  margin: 0;
  min-height: 0;
  outline: none;
  padding: 0;
  width: auto;
}

.coach-report-preview__editable-text--resource-title.coach-report-preview__edit-field:focus,
.coach-report-preview__editable-text--resource-body.coach-report-preview__edit-field:focus,
.coach-report-preview__editable-text--resource-why-chosen.coach-report-preview__edit-field:focus {
  /* Match reportt.html [contenteditable]:focus */
  background: rgba(100, 160, 255, 0.1);
  border-color: transparent;
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(100, 160, 255, 0.25);
  outline: none;
}

.coach-report-preview__resource-why-chosen .coach-report-preview__char-limit {
  display: block;
  margin-left: auto;
  margin-top: 6px;
  text-align: right;
  width: fit-content;
}

.coach-report-preview__admin-layout {
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.coach-report-preview__two-column-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.coach-report-preview__wise-layout {
  align-items: stretch;
  box-sizing: border-box;
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr);
  margin-bottom: 18px;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  width: 100%;
}

.coach-report-preview__wise-section {
  max-width: 100%;
  min-width: 0;
  width: 100%;
}

.coach-report-preview__wise-grid-column,
.coach-report-preview__wise-side-column {
  display: flex;
  min-width: 0;
}

.coach-report-preview__admin-column {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.coach-report-preview__two-column-grid--comparison {
  align-items: stretch;
  gap: 12px;
}

.coach-report-preview__comparison-pair {
  width: 100%;
}

.coach-report-preview__comparison-pair-headers,
.coach-report-preview__comparison-pair-summaries {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.coach-report-preview__comparison-pair-summaries {
  margin-top: 12px;
}

.coach-report-preview__comparison-pair-summaries .coach-report-preview__summary-box {
  padding: 18px 14px 12px;
}

.coach-report-preview__comparison-pair-headers {
  margin-bottom: 0;
}

.coach-report-preview__comparison-pair-headers .coach-report-preview__section-header {
  margin: 2px 0 8px;
}

.coach-report-preview__comparison-table-wrap--pair {
  margin-bottom: 12px;
}

.coach-report-preview__comparison-table--pair col.coach-report-preview__comparison-pair-col-item {
  width: auto;
}

.coach-report-preview__comparison-table--pair col.coach-report-preview__comparison-pair-col-score {
  width: 78px;
}

.coach-report-preview__comparison-table--pair col.coach-report-preview__comparison-pair-col-gap {
  width: 12px;
}

.coach-report-preview__comparison-table--pair th.coach-report-preview__comparison-pair-gap,
.coach-report-preview__comparison-table--pair td.coach-report-preview__comparison-pair-gap {
  background: #ffffff !important;
  border: none !important;
  color: transparent !important;
  padding: 0 !important;
  width: 12px;
}

.coach-report-preview__comparison-table--pair thead th.coach-report-preview__comparison-pair-gap,
.coach-report-preview__comparison-table--pair tfoot td.coach-report-preview__comparison-pair-gap {
  background: #ffffff !important;
}

.coach-report-preview__comparison-block {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;
}

.coach-report-preview__comparison-block .coach-report-preview__comparison-table-wrap {
  flex: 0 0 auto;
}

.coach-report-preview__comparison-block .coach-report-preview__summary-box {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  margin-top: auto;
}

.coach-report-preview__leader-panel,
.coach-report-preview__admin-panel {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  padding: 14px;
  width: 100%;
}

.coach-report-preview__leader-panel--chart {
  align-items: center;
  display: flex;
  justify-content: center;
  overflow: hidden;
  padding: 12px;
}

.coach-report-preview__grid-placement-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  box-shadow: none;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 14px 16px;
  width: 100%;
}

.coach-report-preview__leader-grid {
  margin: 0 auto;
  max-width: 100%;
  width: 100%;
}

.coach-report-preview__leader-grid .wlg-chart-wrap {
  aspect-ratio: 1 / 1;
  max-width: 100%;
  overflow: hidden;
  position: relative;
  width: 100%;
}

.coach-report-preview__leader-grid .wlg-chart-wrap svg {
  display: block;
  height: 100%;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
}

.coach-report-preview__leader-grid-placeholder {
  align-items: center;
  background: linear-gradient(
    135deg,
    rgba(var(--coach-report-accent-rgb), 0.12) 0%,
    rgba(var(--coach-report-accent-rgb), 0.28) 100%
  );
  border: 1px dashed rgba(var(--coach-report-accent-rgb), 0.55);
  border-radius: 10px;
  color: #1a2a3a;
  display: flex;
  font-size: 15px;
  font-weight: 800;
  height: 260px;
  justify-content: center;
  margin-bottom: 12px;
}

.coach-report-preview__grid-placement-label {
  color: #64748b;
  font-size: 7pt;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-bottom: 14px;
  text-transform: uppercase;
}

.coach-report-preview__grid-metric-row {
  align-items: baseline;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  margin-bottom: 10px;
  min-width: 0;
}

.coach-report-preview__grid-metric-name {
  color: #334155;
  font-size: 8pt;
  font-weight: 500;
  min-width: 0;
}

.coach-report-preview__grid-metric-value {
  flex-shrink: 0;
  font-size: 9pt;
  font-weight: 700;
  white-space: nowrap;
}

.coach-report-preview__grid-quadrant-box {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-top: 6px;
  min-width: 0;
  overflow: hidden;
  padding: 10px 12px;
}

.coach-report-preview__grid-quadrant-title {
  color: #0f2c4c;
  font-size: 7.5pt;
  font-weight: 700;
  margin: 0 0 8px;
  overflow-wrap: break-word;
  word-break: normal;
}

.coach-report-preview__grid-quadrant-body {
  color: #334155;
  font-size: 7pt;
  line-height: 1.55;
  margin: 0;
  overflow-wrap: break-word;
  word-break: normal;
}

.coach-report-preview__grid-placement-text {
  color: #334155;
  font-size: 7pt;
  line-height: 1.6;
}

.coach-report-preview__admin-panel--full {
  width: 100%;
}

.coach-report-preview__admin-comments-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-left: 4px solid var(--coach-report-accent);
  border-radius: 9px;
  box-shadow: none;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 14px;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  padding: 14px 16px;
  position: relative;
  width: 100%;
}

.coach-report-preview__admin-comments-card--fill {
  flex: 0 0 auto;
  min-height: 0;
}

.coach-report-preview__admin-comments-body-wrap {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  max-width: 100%;
  min-height: 0;
  min-width: 0;
  width: 100%;
}

.coach-report-preview__admin-comments-body-wrap .coach-report-preview__admin-comments-body {
  flex: 0 0 auto;
  height: auto;
  min-height: 44px;
  resize: none;
}

/* Prefer border-left (prints cleanly). Keep ::before off. */
.coach-report-preview__admin-comments-card::before {
  content: none;
  display: none;
}

.coach-report-preview__admin-comments-label {
  color: #1a2a3a;
  font-size: 6.5pt;
  font-weight: 700;
  letter-spacing: 0.6px;
  margin: 0 0 6px;
  text-transform: uppercase;
}

.coach-report-preview__admin-comments-body:focus,
.coach-report-preview__admin-comments-signature:focus {
  /* Match reportt.html [contenteditable]:focus */
  background: rgba(100, 160, 255, 0.1);
  border-color: transparent;
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(100, 160, 255, 0.25);
  outline: none;
}

.coach-report-preview__admin-comments-body,
.coach-report-preview__admin-comments-signature {
  box-sizing: border-box;
  display: block;
  font-family: inherit;
  max-width: 100%;
  min-width: 0;
  overflow: hidden;
  resize: none;
  width: 100%;
}

.coach-report-preview__admin-comments-body {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  color: #334155;
  font-size: 9pt;
  font-style: italic;
  line-height: 1.6;
  min-height: 44px;
  padding: 4px 6px;
  white-space: pre-wrap;
  word-break: break-word;
}

.coach-report-preview__admin-comments-signature {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  color: #1a2a3a;
  font-size: 8pt;
  font-weight: 700;
  line-height: 1.35;
  margin-top: 7px;
  min-height: 28px;
  padding: 2px 6px;
  white-space: pre-wrap;
  word-break: break-word;
}

.coach-report-preview__comment-section {
  margin-bottom: 0;
  margin-top: 10px;
}

.coach-report-preview__comment-summaries-col > .coach-report-preview__comment-section:first-child {
  margin-top: 0;
}

.coach-report-preview__comment-summaries-headers {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 10px;
}

.coach-report-preview__comment-summaries-headers .coach-report-preview__section-header {
  align-items: center;
  display: flex;
  margin-top: 0;
}

.coach-report-preview__comment-pair-list,
.coach-report-preview__comment-summaries-block {
  width: 100%;
}

.coach-report-preview__comment-summaries-keep {
  width: 100%;
}

.coach-report-preview__comment-summaries-columns {
  align-items: start;
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  width: 100%;
}

.coach-report-preview__comment-summaries-col {
  min-width: 0;
}

.coach-report-preview__comment-pair-table {
  border-collapse: separate;
  border-spacing: 12px 0;
  /* Cancel outer border-spacing so left column aligns with section bar (matches right). */
  margin: 0 -12px 20px;
  table-layout: fixed;
  width: calc(100% + 24px);
}

.coach-report-preview__comment-pair-table td {
  vertical-align: top;
  width: 50%;
}

.coach-report-preview__comment-pair-questions td {
  padding-bottom: 0;
}

.coach-report-preview__comment-pair-questions .coach-report-preview__comment-question {
  margin-bottom: 10px;
}

.coach-report-preview__comment-pair-bodies td {
  padding-top: 0;
}

.coach-report-preview__comment-question {
  color: #2d2a26;
  font-size: 7.5pt;
  font-weight: 700;
  letter-spacing: 0.03em;
  margin-bottom: 5px;
  padding-bottom: 4px;
  border-bottom: 1px solid var(--coach-report-accent-tint);
  text-transform: uppercase;
}

.coach-report-preview__comment-question--coach {
  color: #1a2a3a;
  border-bottom-color: #e2e8f0;
}

.coach-report-preview__comment-items {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.coach-report-preview__comment-item {
    display: flex;
    gap: 8px;
    align-items: flex-start;
    padding: 6px 10px;
    background: #f8fafc;
    border-radius: 6px;
    border-left: 2px solid #bae6fd;
}

.coach-report-preview__comment-item--coach {
  border-left-color: #a78bfa;
}

.coach-report-preview__comment-num {
  align-items: flex-start;
  color: var(--coach-report-accent);
  display: flex;
  flex-shrink: 0;
  font-size: 7.5pt;
  font-weight: 700;
  justify-content: flex-start;
  line-height: 1.45;
  min-width: 1.1em;
  padding: 0;
  width: auto;
}

.coach-report-preview__comment-text {
  flex: 1;
  line-height: 1.45;
  min-width: 0;
}

.coach-report-preview__comment-title {
  color: #1a2a3a;
  font-size: 7.5pt;
  font-weight: 700;
}

.coach-report-preview__comment-body {
  color: #334155;
  font-size: 7.5pt;
  font-weight: 400;
}

.coach-report-preview__comment-placeholder-box {
  align-items: center;
  background: #f8fafc;
  border: none;
  border-radius: 8px;
  box-sizing: border-box;
  color: #94a3b8;
  display: flex;
  font-size: 7.5pt;
  font-style: italic;
  height: 52px;
  justify-content: center;
  max-height: 52px;
  min-height: 52px;
  padding: 12px 14px;
  text-align: center;
  border: 1.5px dashed #e2e8f0;
}

.coach-report-preview__final-note-title {
  color: #1a2a3a;
  font-size: 8pt;
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.coach-report-preview__commitment-box {
  background: #f8fafc;
  border: 1.5px dashed #e2e8f0;
  border-radius: 9px;
  box-shadow: none;
  box-sizing: border-box;
  overflow: visible;
  padding: 20px;
}

.coach-report-preview__editable-field-wrap {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-width: 0;
  position: relative;
  width: 100%;
}

.coach-report-preview__char-limit {
  align-self: flex-end;
  color: #64748b;
  flex: 0 0 auto;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 6pt;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1;
  margin-top: 4px;
  padding: 0;
  pointer-events: none;
  text-align: right;
  white-space: nowrap;
}

.coach-report-preview__editable-field-wrap {
  position: relative;
  width: 100%;
  margin-bottom:4px;
}

.coach-report-preview__editable-field-surface {
  position: relative;
  width: 100%;
}

.coach-report-preview__editable-field-wrap .coach-report-preview__edit-field {
  box-sizing: border-box;
  position: relative;
  width: 100%;
  z-index: 0;
}

.coach-report-preview__editable-text {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
  box-sizing: border-box;
  color: #334155;
  font-family: inherit;
  font-size: 8.5pt;
  line-height: 1.55;
  min-height: 40px;
  outline: none;
  padding: 4px 2px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  white-space: pre-wrap;
  word-break: break-word;
  width: 100%;
}

.coach-report-preview__print-text {
  display: none;
}

/*
 * Modal / screen: textarea owns height; print-text overlays in the same box.
 * Avoids stacked/grid double-height empty space while keeping bold score preview.
 */
@media screen {
  .coach-report-preview__editable-field-surface
    > .coach-report-preview__print-text {
    bottom: 0;
    box-sizing: border-box;
    cursor: text;
    display: block;
    left: 0;
    overflow: hidden;
    pointer-events: auto;
    position: absolute;
    right: 0;
    top: 0;
    white-space: pre-wrap;
    word-break: break-word;
    z-index: 1;
  }

  .coach-report-preview__highlight-card
    .coach-report-preview__editable-field-surface
    > .coach-report-preview__print-text {
    overflow: hidden;
  }

  .coach-report-preview__editable-field-surface
    > .coach-report-preview__edit-field {
    box-sizing: border-box;
    display: block;
    height: auto;
    overflow: hidden;
    position: relative;
    resize: none;
    width: 100%;
    z-index: 0;
  }

  .coach-report-preview__editable-field-wrap:not(:focus-within)
    .coach-report-preview__edit-field {
    -webkit-text-fill-color: transparent;
    caret-color: transparent;
    color: transparent !important;
  }

  .coach-report-preview__editable-field-wrap:not(:focus-within)
    .coach-report-preview__edit-field::placeholder {
    color: transparent !important;
    opacity: 0 !important;
  }

  .coach-report-preview__editable-field-wrap:focus-within
    .coach-report-preview__edit-field {
    -webkit-text-fill-color: unset;
    caret-color: auto;
    color: unset;
  }

  .coach-report-preview__editable-field-wrap:focus-within
    .coach-report-preview__editable-field-surface
    > .coach-report-preview__print-text {
    opacity: 0;
    pointer-events: none;
  }

  /* Lock paragraph font-size focused = unfocused (no resize/shift/cutoff). */
  .coach-report-preview__suggestion-item
    .coach-report-preview__editable-text--suggestion,
  .coach-report-preview__suggestion-item
    .coach-report-preview__editable-text--suggestion:focus {
    font-size: 7.5pt;
    line-height: 1.45;
  }

  .coach-report-preview__editable-text--highlight,
  .coach-report-preview__editable-text--highlight:focus {
    font-size: 7pt;
    line-height: 1.55;
  }

  .coach-report-preview__highlight-card .coach-report-preview__editable-text--highlight,
  .coach-report-preview__highlight-card .coach-report-preview__editable-text--highlight:focus {
    max-height: none;
  }

  .coach-report-preview__editable-text--summary,
  .coach-report-preview__editable-text--summary:focus,
  .coach-report-preview__editable-text--parent-summary,
  .coach-report-preview__editable-text--parent-summary:focus {
    font-size: 8.5pt;
    line-height: 1.55;
  }

  .coach-report-preview__editable-text--final-note,
  .coach-report-preview__editable-text--final-note:focus,
  .coach-report-preview__editable-text--commitments,
  .coach-report-preview__editable-text--commitments:focus {
    font-size: 8.5pt;
    line-height: 1.55;
  }

  .coach-report-preview__admin-comments-body,
  .coach-report-preview__admin-comments-body:focus {
    font-size: 9pt;
    line-height: 1.45;
  }

  .coach-report-preview__admin-comments-signature,
  .coach-report-preview__admin-comments-signature:focus {
    font-size: 8pt;
    line-height: 1.35;
  }

  .coach-report-preview__editable-text--resource-title,
  .coach-report-preview__editable-text--resource-title:focus {
    font-size: 9pt;
    font-weight: 700;
    line-height: 1.35;
  }

  .coach-report-preview__editable-text--resource-body,
  .coach-report-preview__editable-text--resource-body:focus,
  .coach-report-preview__editable-text--resource-why-chosen,
  .coach-report-preview__editable-text--resource-why-chosen:focus {
    font-size: 7.5pt;
    line-height: 1.55;
  }

  /* Keep why-chosen edit field in flow — no show/hide swap (avoids jump/cutoff). */
  .coach-report-preview__resource-why-chosen-para
    > .coach-report-preview__print-text {
    display: none !important;
  }
}

/* Match reportt.html [contenteditable]:hover / :focus */
.coach-report-preview__editable-text:hover,
.coach-report-preview__highlight-num-input:hover,
.coach-report-preview__highlight-title-input:hover,
.coach-report-preview__suggestion-title-input:hover,
.coach-report-preview__comparison-label-input:hover,
.coach-report-preview__comparison-score:hover,
.coach-report-preview__comparison-summary-body:hover,
.coach-report-preview__admin-comments-body:hover,
.coach-report-preview__admin-comments-signature:hover {
  background: rgba(100, 160, 255, 0.06);
  border-radius: 4px;
}

.coach-report-preview__editable-text:focus {
  background: rgba(100, 160, 255, 0.1);
  border-color: transparent;
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(100, 160, 255, 0.25);
  outline: none;
}

.coach-report-preview__editable-text:empty:before {
  color: #94a3b8;
  content: attr(data-placeholder);
  pointer-events: none;
}

.coach-report-preview__editable-text[contenteditable="true"] {
  cursor: text;
}

.coach-report-preview__editable-text[contenteditable="false"] {
  cursor: default;
}

.coach-report-preview__editable-text--summary {
  height: auto;
  max-height: none;
  min-height: 0;
  overflow: hidden;
  resize: none;
}

.coach-report-preview__editable-text--highlight {
  box-sizing: border-box;
  flex: 0 0 auto;
  font-size: 7pt;
  height: auto;
  line-height: 1.55;
  max-height: none;
  min-height: 0;
  overflow: hidden;
  resize: none;
  width: 100%;
}

/* Strength cards only: content-sized editable box; full text always visible. */
.coach-report-preview__highlight-card .coach-report-preview__editable-text--highlight {
  max-height: none;
  overflow: hidden;
  overflow-x: hidden;
  overflow-y: hidden;
  /* Match reportt.html title→body spacing (no extra top pad after label). */
  padding-top: 0;
  /*
   * Bottom padding keeps the last line (and descenders) fully visible
   * and clear of the letter counter below.
   */
  padding-bottom: 10px;
}

.coach-report-preview__editable-text--suggestion {
  height: auto;
  max-height: none;
  min-height: 0;
  overflow: hidden;
  resize: none;
}

.coach-report-preview__editable-text--admin {
  min-height: 120px;
  overflow: hidden;
  resize: none;
}

.coach-report-preview__editable-text--signature {
  margin-top: 10px;
  min-height: 52px;
  overflow: hidden;
  resize: none;
}

.coach-report-preview__editable-text--parent-summary {
  height: auto;
  max-height: none;
  min-height: 140px;
  overflow: hidden;
  resize: none;
}

.coach-report-preview__editable-text--final-note {
  font-size: 8.5pt;
  height: auto;
  max-height: none;
  min-height: 0;
  overflow: hidden;
  resize: none;
}

.coach-report-preview__editable-text--commitments {
  background: repeating-linear-gradient(
    transparent,
    transparent calc(2.2em - 1px),
    #cbd5e1 calc(2.2em - 1px),
    #cbd5e1 calc(2.2em)
  );
  border: none;
  border-radius: 0;
  box-sizing: border-box;
  color: #334155;
  font-size: 8.5pt;
  height: auto;
  line-height: 2.2;
  max-height: 220px;
  min-height: 160px;
  overflow: hidden;
  padding: 2px 0 0;
  resize: none;
  width: 100%;
}

.coach-report-preview__editable-text--commitments:focus {
  /* Match reportt.html [contenteditable]:focus */
  background: repeating-linear-gradient(
      transparent,
      transparent calc(2.2em - 1px),
      #cbd5e1 calc(2.2em - 1px),
      #cbd5e1 calc(2.2em)
    ),
    rgba(100, 160, 255, 0.1);
  border-color: transparent;
  border-radius: 4px;
  box-shadow: 0 0 0 2px rgba(100, 160, 255, 0.25);
  outline: none;
}

@media screen and (max-width: 900px) {
  .coach-report-preview__page {
    max-width: 100%;
    min-height: auto;
  }

  .coach-report-preview__page-footer {
    position: static;
  }

  .coach-report-preview__page-content {
    padding-bottom: 24px;
  }

  .coach-report-preview__wise-layout {
    grid-template-columns: 1fr;
  }

  .coach-report-preview__kpi-grid,
  .coach-report-preview__highlight-grid,
  .coach-report-preview__resource-grid,
  .coach-report-preview__admin-layout,
  .coach-report-preview__wise-layout,
  .coach-report-preview__leader-grid,
  .coach-report-preview__leader-grid .wlg-chart-wrap {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .coach-report-preview__leader-grid .wlg-chart-wrap {
    aspect-ratio: auto !important;
    height: 3.4in !important;
    max-width: 100% !important;
    width: 100% !important;
  }
  .coach-report-preview__two-column-grid {
    grid-template-columns: 1fr;
  }

  .coach-report-preview__comment-summaries-headers {
    grid-template-columns: 1fr;
  }

  .coach-report-preview__comment-pair-table {
    border-spacing: 0;
    display: block;
  }

  .coach-report-preview__comment-pair-table tbody,
  .coach-report-preview__comment-pair-table tr,
  .coach-report-preview__comment-pair-table td {
    display: block;
    width: 100% !important;
  }
}

@media screen and (max-width: 640px) {
  .coach-report-preview__report-header,
  .coach-report-preview__running-header,
  .coach-report-preview__data-section-banner,
  .coach-report-preview__page-content,
  .coach-report-preview__page-footer {
    padding-left: 18px;
    padding-right: 18px;
  }

  .coach-report-preview__header-meta {
    flex-direction: column;
    gap: 12px;
  }

  .coach-report-preview__header-top {
    gap: 14px;
  }

  .coach-report-preview__school-name {
    font-size: 28px;
  }

  .coach-report-preview__section-title {
    font-size: 20px;
  }

  .coach-report-preview__kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .coach-report-preview__suggestion-item {
    flex-direction: column;
  }
}

@media print {
  /* Isolate report: hide app chrome / modal chrome, print all preview pages. */
  html,
  body {
    background: #ffffff !important;
    height: auto !important;
    margin: 0 !important;
    overflow: visible !important;
    padding: 0 !important;
    width: 100% !important;
  }

  body.coach-report-printing * {
    visibility: hidden !important;
  }

  body.coach-report-printing .coach-report-preview,
  body.coach-report-printing .coach-report-preview * {
    visibility: visible !important;
  }

  body.coach-report-printing .MuiModal-root,
  body.coach-report-printing .MuiModal-root > *,
  body.coach-report-printing [role="presentation"],
  body.coach-report-printing [role="presentation"] > * {
    background: transparent !important;
    box-shadow: none !important;
    height: auto !important;
    left: 0 !important;
    margin: 0 !important;
    max-height: none !important;
    max-width: none !important;
    overflow: visible !important;
    padding: 0 !important;
    position: static !important;
    right: auto !important;
    top: 0 !important;
    transform: none !important;
    width: 100% !important;
  }

  body.coach-report-printing .MuiBackdrop-root {
    display: none !important;
  }

  .coach-report-preview,
  .coach-report-preview * {
    /* System fonts embed reliably in Chrome PDF; custom fonts corrupt glyphs. */
    font-family: system-ui, -apple-system, "Segoe UI", sans-serif !important;
    letter-spacing: normal !important;
    overflow-wrap: break-word !important;
    word-break: normal !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .coach-report-preview {
    color: #334155 !important;
    left: 0 !important;
    margin: 0 !important;
    max-width: 100% !important;
    overflow-x: hidden !important;
    padding: 0 !important;
    position: absolute !important;
    top: 0 !important;
    width: 100% !important;
  }

  .coach-report-preview__toolbar {
    display: none !important;
  }

  .coach-report-preview .pull-quote-text::before,
  .coach-report-preview__suggestion-quote::before {
    font-family: Georgia, "Times New Roman", serif !important;
  }

  .coach-report-preview__school-name {
    font-weight: 900 !important;
  }

  .coach-report-preview__section-title,
  .coach-report-preview__kpi-value {
    font-weight: 800 !important;
  }

  .coach-report-preview__grid-quadrant-title,
  .coach-report-preview__grid-metric-value {
    font-weight: 700 !important;
  }

  /* Fixed footer repeats on every printed sheet; no min-height sheet padding. */
  .coach-report-preview__page,
  .coach-report-preview__page--fill {
    background: #ffffff !important;
    break-after: page !important;
    box-sizing: border-box !important;
    display: block !important;
    height: auto !important;
    margin: 0 auto !important;
    max-height: none !important;
    max-width: 100% !important;
    min-height: 0 !important;
    overflow: visible !important;
    page-break-after: always !important;
    padding: 0 !important;
    position: relative !important;
    width: 100% !important;
  }

  .coach-report-preview__page:last-of-type,
  .coach-report-preview__page--fill:last-of-type,
  .coach-report-preview__page:last-child {
    break-after: auto !important;
    page-break-after: auto !important;
  }

  /* Keep modal header exactly as on screen. */
  .coach-report-preview__running-header {
    align-items: center !important;
    background: var(--coach-report-accent) !important;
    color: #ffffff !important;
    display: flex !important;
    flex: 0 0 auto !important;
    justify-content: space-between !important;
    padding: 8px 20px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* Footers render via @page margin boxes (counter(page) does not work on position:fixed). */
  .coach-report-preview__page-footer,
  .coach-report-print-fixed-footer,
  .coach-report-sheet-footer {
    display: none !important;
  }

  .coach-report-preview__page-content,
  .coach-report-preview__page-content--fill {
    break-before: auto !important;
    display: block !important;
    overflow: visible !important;
    padding: 6px 20px 0 !important;
    page-break-before: auto !important;
  }

  .coach-report-preview__report-header,
  .coach-report-preview__data-section-banner {
    padding-left: 20px !important;
    padding-right: 20px !important;
  }

  .coach-report-preview__running-header {
    padding: 8px 20px !important;
  }

  .coach-report-preview__report-header {
    padding-top: 6px !important;
  }

  .coach-report-preview__header-top {
    margin-bottom: 6px !important;
  }

  .coach-report-preview__header-meta {
    padding: 6px 0 6px !important;
  }

  .coach-report-preview__section-header {
    margin: 8px 0 6px !important;
  }

  /* Top spacing only on Expectations / Relational Warmth print page. */
  .coach-report-preview__page--expectations .coach-report-preview__page-content {
    padding-top: 8px !important;
  }

  .coach-report-preview__page--expectations .coach-report-preview__score-legend {
    margin-bottom: 8px !important;
    margin-top: 0 !important;
  }

  .coach-report-preview__comparison-pair-headers .coach-report-preview__section-header {
    margin: 2px 0 8px !important;
  }

  .coach-report-preview__overall-summary-box {
    margin-bottom: 0 !important;
    padding: 2px 0 !important;
  }

  .coach-report-preview__school-name {
    font-size: 21pt !important;
    line-height: 1.1 !important;
  }

  .coach-report-preview__header-top {
    align-items: flex-end !important;
  }

  .coach-report-preview__school-logo {
    border-radius: 10px !important;
    height: 52px !important;
    width: 52px !important;
  }

  .coach-report-preview__report-subtitle {
    font-size: 10.5pt !important;
    line-height: 1.2 !important;
  }

  .coach-report-preview__section-title {
    font-size: 13pt !important;
  }

  .coach-report-preview__meta-label,
  .coach-report-preview__summary-box-label,
  .coach-report-preview__admin-label,
  .coach-report-preview__admin-comments-label {
    font-size: 6.5pt !important;
  }

  .coach-report-preview__kpi-label {
    font-size: 7pt !important;
  }

  .coach-report-preview__meta-value {
    font-size: 9.5pt !important;
  }

  .coach-report-preview__kpi-grid {
    gap: 8px !important;
    grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
    break-inside: auto !important;
    margin-bottom: 8px !important;
    margin-top: 6px !important;
    page-break-inside: auto !important;
  }

  .coach-report-preview__highlight-grid {
    display: grid !important;
    gap: 9px !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    margin-bottom: 10px !important;
    break-inside: auto !important;
    page-break-inside: auto !important;
  }

  .coach-report-preview__resource-grid {
    display: grid !important;
    gap: 8px !important;
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    margin-bottom: 0 !important;
    break-inside: auto !important;
    page-break-inside: auto !important;
  }

  .coach-report-preview__connectedness-block {
    break-before: auto !important;
    break-inside: auto !important;
    margin-bottom: 0 !important;
    margin-top: 6px !important;
    page-break-before: auto !important;
    page-break-inside: auto !important;
  }

  /*
   * Closing block is outside the marker table and may start in leftover
   * space (break-before:auto). Do not use break-before:avoid on the total
   * row — that pulled the last marker row onto the next page.
   */
  .coach-report-preview__connectedness-closing {
    break-before: auto !important;
    break-inside: avoid !important;
    page-break-before: auto !important;
    page-break-inside: avoid !important;
  }

  .coach-report-preview__connectedness-closing .coach-report-preview__data-table {
    margin: 0 !important;
  }

  .coach-report-preview__connectedness-block .coach-report-preview__data-table tr {
    break-inside: auto !important;
    page-break-inside: auto !important;
  }

  .coach-report-preview__connectedness-footer-note {
    break-before: avoid !important;
    page-break-before: avoid !important;
  }

  /* Keep desktop 2-column comments in print (same as modal). */
  .coach-report-preview__two-column-grid:not(.coach-report-preview__two-column-grid--comparison) {
    border-collapse: separate !important;
    border-spacing: 12px 0 !important;
    display: table !important;
    table-layout: fixed !important;
    width: 100% !important;
  }

  .coach-report-preview__two-column-grid:not(.coach-report-preview__two-column-grid--comparison) > * {
    display: table-cell !important;
    margin-bottom: 0 !important;
    vertical-align: top !important;
    width: 50% !important;
  }

  /* Like reportGeneratemodal: keep question + its cards together (no orphan headers). */
  .coach-report-preview__comment-section {
    break-inside: avoid !important;
    margin-bottom: 0 !important;
    margin-top: 10px !important;
    page-break-inside: avoid !important;
  }

  .coach-report-preview__comment-summaries-col > .coach-report-preview__comment-section:first-child {
    margin-top: 0 !important;
  }

  .coach-report-preview__comment-summaries-headers {
    border-collapse: separate !important;
    border-spacing: 14px 0 !important;
    display: table !important;
    margin-bottom: 10px !important;
    table-layout: fixed !important;
    width: 100% !important;
  }

  .coach-report-preview__comment-summaries-headers > * {
    display: table-cell !important;
    vertical-align: top !important;
    width: 50% !important;
  }

  .coach-report-preview__comment-summaries-headers .coach-report-preview__section-header {
    align-items: center !important;
    display: flex !important;
    flex-direction: row !important;
  }

  .coach-report-preview__comment-summaries-headers .coach-report-preview__section-bar {
    align-self: stretch !important;
    display: block !important;
    flex: 0 0 4px !important;
    width: 4px !important;
  }

  .coach-report-preview__comment-summaries-headers .coach-report-preview__section-title {
    display: block !important;
  }

  .coach-report-preview__comment-pair-table {
    border-collapse: separate !important;
    border-spacing: 12px 0 !important;
    break-inside: avoid !important;
    display: table !important;
    margin-bottom: 4px !important;
    margin-left: -12px !important;
    margin-right: -12px !important;
    page-break-inside: avoid !important;
    table-layout: fixed !important;
    width: calc(100% + 24px) !important;
  }

  .coach-report-preview__comment-pair-table td {
    vertical-align: top !important;
    width: 50% !important;
  }

  .coach-report-preview__comment-question {
    break-after: avoid !important;
    page-break-after: avoid !important;
  }

  .coach-report-preview__comment-pair-questions {
    break-after: avoid !important;
    page-break-after: avoid !important;
  }

  .coach-report-preview__comment-summaries-headers {
    break-after: avoid !important;
    page-break-after: avoid !important;
  }

  .coach-report-preview__comment-summaries-keep {
    -webkit-column-break-inside: avoid !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  .coach-report-preview__comment-summaries-block,
  .coach-report-preview__comment-pair-list {
    break-inside: auto !important;
    page-break-inside: auto !important;
  }

  .coach-report-preview__comment-item {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
    padding: 6px 10px 6px 6px !important;
  }

  .coach-report-preview__comment-num {
    min-width: 1.1em !important;
    padding: 0 !important;
    width: auto !important;
  }

  .coach-report-preview__connectedness-block .coach-report-preview__section-header {
    margin-bottom: 4px !important;
    margin-top: 0 !important;
  }

  .coach-report-preview__connectedness-block .coach-report-preview__intro-text {
    margin-bottom: 6px !important;
  }

  .coach-report-preview__connectedness-footer-note {
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    break-before: avoid !important;
    font-size: 12px !important;
    height: auto !important;
    line-height: 1.45 !important;
    margin-bottom: 0 !important;
    margin-top: 4px !important;
    max-width: 100% !important;
    min-height: 0 !important;
    page-break-before: avoid !important;
    padding: 0 !important;
    white-space: normal !important;
    width: 100% !important;
    word-break: normal !important;
  }

  .coach-report-preview__data-table {
    border-collapse: collapse !important;
    display: table !important;
    table-layout: fixed !important;
    width: 100% !important;
  }

  .coach-report-preview__data-table thead {
    display: table-row-group !important;
  }

  .coach-report-preview__data-table tbody {
    display: table-row-group !important;
  }

  /* Avoid Chrome print repeating thead/tfoot on every sheet. */
  .coach-report-preview__data-table tfoot,
  .coach-report-preview__comparison-table thead,
  .coach-report-preview__comparison-table tfoot {
    display: table-row-group !important;
  }

  .coach-report-preview__data-table thead th,
  .coach-report-preview__data-table tfoot td,
  .coach-report-preview__connectedness-total-row td {
    line-height: 1.4 !important;
    padding: 10px 12px !important;
  }

  .coach-report-preview__data-table thead th:first-child,
  .coach-report-preview__data-table tbody td:first-child,
  .coach-report-preview__data-table tfoot td:first-child {
    text-align: left !important;
  }

  .coach-report-preview__data-table th,
  .coach-report-preview__data-table td {
    overflow: visible !important;
  }

  .coach-report-preview__data-table thead th:last-child,
  .coach-report-preview__data-table td:last-child,
  .coach-report-preview__data-table tfoot td:last-child,
  .coach-report-preview__data-table th.coach-report-preview__score-heading,
  .coach-report-preview__data-table td.coach-report-preview__score-col {
    text-align: center !important;
    white-space: nowrap !important;
    width: 100px !important;
  }

  .coach-report-preview__connectedness-block .coach-report-preview__data-table thead th:last-child,
  .coach-report-preview__connectedness-block .coach-report-preview__data-table td:last-child,
  .coach-report-preview__connectedness-block .coach-report-preview__data-table tfoot td:last-child,
  .coach-report-preview__connectedness-block .coach-report-preview__data-table th.coach-report-preview__score-heading,
  .coach-report-preview__connectedness-block .coach-report-preview__data-table td.coach-report-preview__score-col {
    width: 200px !important;
  }

  .coach-report-preview__score-cell {
    align-items: center !important;
    display: inline-flex !important;
    gap: 5px !important;
    justify-content: center !important;
    max-width: 100% !important;
    width: 100% !important;
  }

  .coach-report-preview__score-cell--message {
    align-items: center !important;
    display: flex !important;
    justify-content: center !important;
    width: 100% !important;
  }

  .coach-report-preview__connectedness-score-text {
    font-size: 7.5pt !important;
    white-space: nowrap !important;
  }

  .coach-report-preview__connectedness-score-text--message {
    font-size: 11px !important;
    font-weight: 600 !important;
    line-height: 1.3 !important;
    max-width: none !important;
    overflow: visible !important;
    padding-right: 0 !important;
    text-align: center !important;
    white-space: nowrap !important;
    word-break: normal !important;
  }

  .coach-report-preview__score-bar-wrap {
    border-radius: 3px !important;
    flex: 0 0 40px !important;
    height: 5px !important;
    width: 40px !important;
  }

  .coach-report-preview__data-table--parent-feedback .coach-report-preview__score-bar,
  .coach-report-preview__data-table-wrap--parent-feedback .coach-report-preview__score-bar {
    background: linear-gradient(90deg, var(--green) 0%, #34d399 100%) !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .coach-report-preview__score-meter {
    gap: 4px !important;
  }

  .coach-report-preview__comparison-table .coach-report-preview__score-meter {
    display: flex !important;
    justify-content: center !important;
    width: 100% !important;
  }

  .coach-report-preview__comparison-table .coach-report-preview__score-meter-value {
    text-align: center !important;
  }

  .coach-report-preview__score-meter-value {
    font-size: 7pt !important;
  }

  .coach-report-preview__score-meter-track {
    flex: 0 0 12px !important;
    flex-basis: 12px !important;
    height: 5px !important;
    width: 12px !important;
  }

  .coach-report-preview__comparison-table td.coach-report-preview__score-col,
  .coach-report-preview__comparison-table tfoot td.coach-report-preview__score-col {
    text-align: center !important;
    width: 72px !important;
  }

  .coach-report-preview__comparison-table--pair col.coach-report-preview__comparison-pair-col-score {
    width: 72px !important;
  }

  .coach-report-preview__comparison-table--pair col.coach-report-preview__comparison-pair-col-gap,
  .coach-report-preview__comparison-table--pair th.coach-report-preview__comparison-pair-gap,
  .coach-report-preview__comparison-table--pair td.coach-report-preview__comparison-pair-gap {
    width: 12px !important;
  }

  .coach-report-preview__comparison-pair-headers,
  .coach-report-preview__comparison-pair-summaries {
    border-collapse: separate !important;
    border-spacing: 12px 0 !important;
    display: table !important;
    table-layout: fixed !important;
    width: 100% !important;
  }

  /* Cancel outer border-spacing so the left summary aligns with the table edge. */
  .coach-report-preview__comparison-pair-summaries {
    margin-left: -12px !important;
    margin-right: -12px !important;
    margin-top: 12px !important;
    width: calc(100% + 24px) !important;
  }

  .coach-report-preview__comparison-pair-headers > *,
  .coach-report-preview__comparison-pair-summaries > * {
    display: table-cell !important;
    vertical-align: top !important;
    width: 50% !important;
  }

  .coach-report-preview__comparison-pair-summaries .coach-report-preview__summary-box {
    padding: 18px 14px 12px !important;
    text-align: left !important;
    vertical-align: top !important;
  }

  .coach-report-preview__comparison-pair-summaries .coach-report-preview__summary-box-label {
    margin-left: 0 !important;
    margin-top: 0 !important;
    padding-left: 0 !important;
    text-align: left !important;
  }

  .coach-report-preview__admin-layout {
    display: grid !important;
    gap: 18px !important;
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }

  .coach-report-preview__two-column-grid {
    display: grid !important;
    gap: 12px !important;
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }

  /* Keep desktop side-by-side comments in print (table cells fragment more reliably than CSS grid). */
  .coach-report-preview__two-column-grid:not(.coach-report-preview__two-column-grid--comparison) {
    border-collapse: separate !important;
    border-spacing: 12px 0 !important;
    display: table !important;
    table-layout: fixed !important;
    width: 100% !important;
  }

  .coach-report-preview__two-column-grid:not(.coach-report-preview__two-column-grid--comparison) > * {
    display: table-cell !important;
    margin-bottom: 0 !important;
    vertical-align: top !important;
    width: 50% !important;
  }

  /*
   * Keep desktop side-by-side comparison in print, but use table layout.
   * CSS Grid fragments as "headers only" then tables on the next sheet.
   */
  .coach-report-preview__two-column-grid--comparison {
    border-collapse: separate !important;
    border-spacing: 12px 0 !important;
    display: table !important;
    table-layout: fixed !important;
    width: 100% !important;
  }

  .coach-report-preview__comparison-block {
    display: table-cell !important;
    vertical-align: top !important;
    width: 50% !important;
  }

  .coach-report-preview__header-meta {
    flex-direction: row !important;
  }

  .coach-report-preview__suggestion-item {
    flex-direction: row !important;
  }

  .coach-report-preview__kpi-card {
    border-top: 4px solid var(--coach-report-accent) !important;
    display: flex !important;
    flex-direction: column !important;
    justify-content: flex-end !important;
    overflow: hidden !important;
    padding: 6px 6px !important;
  }

  .coach-report-preview__kpi-value {
    font-size: 15pt !important;
    overflow-wrap: break-word !important;
    word-break: break-word !important;
  }

  .coach-report-preview__kpi-value--long {
    font-size: 9.5pt !important;
    letter-spacing: normal !important;
    line-height: 1.2 !important;
    padding-top: 4px !important;
    white-space: normal !important;
  }

  .coach-report-preview__highlight-title,
  .coach-report-preview__suggestion-title {
    font-size: 8.5pt !important;
    line-height: 1.3 !important;
    margin-bottom: 4px !important;
  }

  .coach-report-preview__resource-title,
  .coach-report-preview__resource-title-big {
    font-size: 9pt !important;
    line-height: 1.25 !important;
    margin-bottom: 5px !important;
  }

  .coach-report-preview__resource-list {
    gap: 12px !important;
  }

  .coach-report-preview__resource-card-big {
    padding: 10px 14px !important;
  }

  .coach-report-preview__resource-type-badge {
    margin-bottom: 0 !important;
  }

  .coach-report-preview__resource-card-top {
    margin-bottom: 6px !important;
  }

  .coach-report-preview__resource-card-actions {
    display: none !important;
    margin-bottom: 0 !important;
  }

  .coach-report-preview__suggestion-icon {
    font-size: 8pt !important;
    height: 22px !important;
    margin-top: 0 !important;
    width: 22px !important;
  }

  .coach-report-preview__highlight-body {
    font-size: 7pt !important;
    line-height: 1.45 !important;
  }

  .coach-report-preview__summary-box-text,
  .coach-report-preview__intro-text,
  .coach-report-preview__leader-summary,
  .coach-report-preview__resource-body {
    font-size: 8.5pt !important;
    line-height: 1.45 !important;
  }

  .coach-report-preview__comment-body {
    font-size: 7.5pt !important;
    line-height: 1.45 !important;
  }

  .coach-report-preview__leader-commitments-intro {
    font-size: 8pt !important;
    line-height: 1.45 !important;
  }

  .coach-report-preview__data-table th,
  .coach-report-preview__data-table td {
    font-size: 7.5pt !important;
    line-height: 1.45 !important;
  }

  /* Textareas print garbled in Chrome PDF — hide inputs, show plain text mirrors. */
  .coach-report-preview__edit-field,
  .coach-report-preview__char-limit {
    display: none !important;
  }

  .coach-report-preview__editable-field-surface {
    display: block !important;
    height: auto !important;
    min-height: 0 !important;
    position: static !important;
  }

  .coach-report-preview__editable-field-surface
    > .coach-report-preview__print-text,
  .coach-report-preview__editable-field-surface
    > .coach-report-preview__edit-field {
    bottom: auto !important;
    grid-area: auto !important;
    height: auto !important;
    left: auto !important;
    position: static !important;
    right: auto !important;
    top: auto !important;
    width: 100% !important;
  }

  .coach-report-preview__print-text {
    display: block !important;
    white-space: pre-wrap !important;
    word-break: normal !important;
  }

  .coach-report-preview__resource-why-chosen
    .coach-report-preview__editable-text--resource-why-chosen.coach-report-preview__print-text,
  .coach-report-preview__resource-why-chosen .coach-report-preview__resource-why-chosen-text {
    display: inline !important;
  }

  .coach-report-preview__print-text--placeholder {
    color: #94a3b8 !important;
  }

  .coach-report-preview__editable-field-wrap > .coach-report-preview__print-text {
    display: block !important;
  }

  /* Commitment / note boxes: keep modal look in PDF (incl. empty state). */
  .coach-report-preview__editable-text--commitments,
  .coach-report-preview__editable-text--final-note,
  .coach-report-preview__editable-text--parent-summary {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .coach-report-preview__editable-text--commitments.coach-report-preview__print-text {
    background: #ffffff !important;
    background-image: none !important;
    border: 1px solid #b7d3e8 !important;
    border-radius: 12px !important;
    height: auto !important;
    line-height: 1.55 !important;
    max-height: none !important;
    min-height: 120px !important;
    overflow: visible !important;
    padding: 12px 14px !important;
  }

  .coach-report-preview textarea,
  .coach-report-preview__editable-text {
    box-shadow: none !important;
    font-size: 8.5pt !important;
    line-height: 1.45 !important;
    resize: none !important;
    white-space: pre-wrap !important;
    word-break: normal !important;
  }

  .coach-report-preview__admin-comments-body {
    box-shadow: none !important;
    font-size: 9pt !important;
    line-height: 1.45 !important;
    resize: none !important;
    white-space: pre-wrap !important;
    word-break: normal !important;
  }

  .coach-report-preview__admin-comments-signature {
    box-shadow: none !important;
    font-size: 8pt !important;
    line-height: 1.45 !important;
    resize: none !important;
    white-space: pre-wrap !important;
    word-break: normal !important;
  }

  /* Editable sections size to content in print — avoids empty card bottoms. */
  .coach-report-preview__editable-text--summary {
    height: auto !important;
    max-height: none !important;
    min-height: 0 !important;
    overflow: visible !important;
  }

  .coach-report-preview__editable-text--highlight {
    font-size: 7pt !important;
    height: auto !important;
    max-height: none !important;
    min-height: 0 !important;
    overflow: visible !important;
  }

  .coach-report-preview__highlight-card {
    min-height: 0 !important;
    padding: 8px 11px !important;
  }

  .coach-report-preview__suggestion-item .coach-report-preview__editable-text--suggestion,
  .coach-report-preview__editable-text--suggestion {
    height: auto !important;
    max-height: none !important;
    min-height: 0 !important;
    overflow: visible !important;
  }

  .coach-report-preview__suggestion-list {
    break-inside: auto !important;
    gap: 5px !important;
    page-break-inside: auto !important;
  }

  .coach-report-preview__suggestion-item {
    padding: 12px 14px 12px 12px !important;
  }

  .coach-report-preview__comment-item,
  .coach-report-preview__comment-text,
  .coach-report-preview__comment-title,
  .coach-report-preview__comment-body,
  .coach-report-preview__comment-num {
    line-height: 1.25 !important;
  }


  .coach-report-preview__suggestion-item .coach-report-preview__editable-text--suggestion,
  .coach-report-preview__suggestion-item .coach-report-preview__print-text {
    font-size: 7.5pt !important;
    height: auto !important;
    line-height: 1.35 !important;
    max-height: none !important;
    min-height: 0 !important;
    overflow: visible !important;
  }

  .coach-report-preview__connectedness-block {
    margin-top: 6px !important;
  }

  .coach-report-preview__connectedness-block .coach-report-preview__section-header {
    margin-top: 0 !important;
    margin-bottom: 4px !important;
  }

  .coach-report-preview__editable-text--parent-summary {
    height: auto !important;
    max-height: none !important;
    min-height: 0 !important;
    overflow: visible !important;
  }

  /* Space above Parent Feedback Summary (after Parent Average table). */
  .coach-report-preview__data-table-wrap + .coach-report-preview__summary-box {
    margin-top: 14px !important;
    padding-top: 18px !important;
  }

  .coach-report-preview__editable-text--final-note {
    height: auto !important;
    max-height: none !important;
    min-height: 0 !important;
    overflow: visible !important;
  }

  /* Space above Leader Commitments (after Final Note). */
  .coach-report-preview__final-note-box + .coach-report-preview__section-header {
    margin-top: 16px !important;
  }

  .coach-report-preview__editable-text--commitments {
    height: auto !important;
    max-height: none !important;
    min-height: 120px !important;
    overflow: visible !important;
  }

  /*
   * Graph / fill content stays in normal flow; fixed print footer handles
   * bottom alignment on every sheet.
   */
  .coach-report-preview__page-content--fill {
    display: block !important;
    min-height: 0 !important;
    overflow: visible !important;
  }

  .coach-report-preview__wise-section {
    -webkit-column-break-inside: avoid !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  .coach-report-preview__wise-section .coach-report-preview__section-header {
    break-after: avoid !important;
    page-break-after: avoid !important;
  }

  .coach-report-preview__admin-comments-card--fill,
  .coach-report-preview__admin-comments-body-wrap {
    flex: 0 0 auto !important;
    flex-direction: column !important;
    min-height: 0 !important;
  }

  .coach-report-preview__admin-comments-body-wrap {
    display: block !important;
  }

  .coach-report-preview__admin-comments-body {
    flex: 0 0 auto !important;
    height: auto !important;
    max-height: none !important;
    min-height: 0 !important;
    overflow: visible !important;
  }

  .coach-report-preview__highlight-card,
  .coach-report-preview__suggestion-item,
  .coach-report-preview__resource-card-big,
  .coach-report-preview__summary-box,
  .coach-report-preview__overall-summary-box,
  .coach-report-preview__data-table-wrap,
  .coach-report-preview__comparison-table-wrap,
  .coach-report-preview__admin-comments-card,
  .coach-report-preview__grid-placement-card,
  .coach-report-preview__leader-panel {
    max-height: none !important;
  }

  /* Atomic cards stay whole (reportGeneratemodal .item / .whiteBox pattern). */
  .coach-report-preview__wise-section,
  .coach-report-preview__wise-layout,
  .coach-report-preview__wise-grid-column,
  .coach-report-preview__wise-side-column,
  .coach-report-preview__leader-panel--chart,
  .coach-report-preview__leader-grid,
  .coach-report-preview__leader-grid .wlg-chart-wrap,
  .coach-report-preview__grid-placement-card,
  .coach-report-preview__suggestion-item,
  .coach-report-preview__resource-card-big,
  .coach-report-preview__highlight-card,
  .coach-report-preview__summary-box,
  .coach-report-preview__admin-comments-card,
  .coach-report-preview__admin-comments-card--fill,
  .coach-report-preview__commitment-box,
  .coach-report-preview__final-note-box,
  .coach-report-preview__comment-section,
  .coach-report-preview__comment-item,
  .coach-report-preview__pull-quote,
  .pull-quote {
    -webkit-column-break-inside: avoid !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  .coach-report-preview__comment-question,
  .coach-report-preview__comment-pair-questions,
  .coach-report-preview__comment-summaries-headers,
  .coach-report-preview__section-header,
  .coach-report-preview__score-legend {
    break-after: avoid !important;
    page-break-after: avoid !important;
  }

  .coach-report-preview__comment-summaries-keep {
    -webkit-column-break-inside: avoid !important;
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  .coach-report-preview__suggestion-item {
    background: #f8fafc !important;
    border: 1px solid #e2e8f0 !important;
    border-left: 4px solid var(--coach-report-accent) !important;
    border-radius: 10px !important;
    display: grid !important;
    grid-template-columns: auto 1fr !important;
    overflow: hidden !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .coach-report-preview__suggestion-item::before {
    content: none !important;
    display: none !important;
  }

  .coach-report-preview__resource-card-big,
  .coach-report-preview__comment-item {
    overflow: visible !important;
  }

  .coach-report-preview__comment-summaries-columns {
    align-items: start !important;
    display: grid !important;
    gap: 14px !important;
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }

  .coach-report-preview__comment-section {
    margin-bottom: 0 !important;
    margin-top: 10px !important;
  }

  .coach-report-preview__comment-summaries-col > .coach-report-preview__comment-section:first-child {
    margin-top: 0 !important;
  }

  .coach-report-preview__comment-placeholder-box {
    align-items: center !important;
    box-sizing: border-box !important;
    height: auto !important;
    max-height: none !important;
    min-height: 52px !important;
    overflow: visible !important;
    padding-top: 12px !important;
    padding-bottom: 12px !important;
    position: static !important;
    white-space: normal !important;
    word-break: break-word !important;
  }

  .coach-report-preview__comment-question {
    margin-bottom: 5px !important;
  }

  .coach-report-preview__admin-comments-card,
  .coach-report-preview__admin-comments-card--fill {
    overflow: hidden !important;
  }

  /* Large wrappers may still flow across pages. */
  .coach-report-preview__kpi-grid,
  .coach-report-preview__highlight-grid,
  .coach-report-preview__data-table-wrap,
  .coach-report-preview__comparison-table-wrap,
  .coach-report-preview__comparison-block,
  .coach-report-preview__two-column-grid--comparison,
  .coach-report-preview__connectedness-block,
  .coach-report-preview__suggestion-list {
    break-inside: auto !important;
    overflow: visible !important;
    page-break-inside: auto !important;
  }

  /* No bottom section spacing — prevents leftover lines/footers from landing on a blank sheet. */
  .coach-report-preview__resource-list,
  .coach-report-preview__suggestion-list,
  .coach-report-preview__highlight-grid,
  .coach-report-preview__resource-card-big,
  .coach-report-preview__suggestion-item,
  .coach-report-preview__summary-box,
  .coach-report-preview__overall-summary-box,
  .coach-report-preview__data-table-wrap,
  .coach-report-preview__comparison-table-wrap,
  .coach-report-preview__comment-section,
  .coach-report-preview__comment-pair-table,
  .coach-report-preview__admin-comments-card,
  .coach-report-preview__admin-comments-card--fill,
  .coach-report-preview__pull-quote,
  .pull-quote,
  .coach-report-preview__intro-text,
  .coach-report-preview__connectedness-block {
    margin-bottom: 0 !important;
  }

  .coach-report-preview__comparison-block .coach-report-preview__summary-box {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  .coach-report-preview__comparison-table tr,
  .coach-report-preview__data-table tr {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .coach-report-preview__connectedness-block .coach-report-preview__data-table tr {
    break-inside: auto !important;
    page-break-inside: auto !important;
  }

  .coach-report-preview__connectedness-closing {
    break-before: auto !important;
    break-inside: avoid !important;
    page-break-before: auto !important;
    page-break-inside: avoid !important;
  }

  /* Match on-screen / ReportGenerateModal comparison rows in print. */
  .coach-report-preview__comparison-table-wrap,
  .coach-report-preview__data-table-wrap {
    border-radius: 0 !important;
    overflow: visible !important;
  }

  .coach-report-preview__comparison-table thead tr:first-child th:first-child,
  .coach-report-preview__data-table thead tr:first-child th:first-child {
    border-top-left-radius: 12px !important;
  }

  .coach-report-preview__comparison-table:not(.coach-report-preview__comparison-table--pair)
    thead
    tr:first-child
    th:last-child,
  .coach-report-preview__data-table thead tr:first-child th:last-child {
    border-top-right-radius: 12px !important;
  }

  .coach-report-preview__comparison-table tfoot tr:last-child td:first-child,
  .coach-report-preview__data-table tfoot tr:last-child td:first-child {
    border-bottom-left-radius: 12px !important;
  }

  .coach-report-preview__comparison-table:not(.coach-report-preview__comparison-table--pair)
    tfoot
    tr:last-child
    td:last-child,
  .coach-report-preview__data-table tfoot tr:last-child td:last-child {
    border-bottom-right-radius: 12px !important;
  }

  .coach-report-preview__comparison-table--pair thead tr:first-child th:nth-child(3) {
    border-top-right-radius: 12px !important;
  }

  .coach-report-preview__comparison-table--pair thead tr:first-child th:nth-child(5) {
    border-top-left-radius: 12px !important;
  }

  .coach-report-preview__comparison-table--pair thead tr:first-child th:last-child {
    border-top-right-radius: 12px !important;
  }

  .coach-report-preview__comparison-table--pair tfoot tr:last-child td:nth-child(3) {
    border-bottom-right-radius: 12px !important;
  }

  .coach-report-preview__comparison-table--pair tfoot tr:last-child td:nth-child(5) {
    border-bottom-left-radius: 12px !important;
  }

  .coach-report-preview__comparison-table--pair tfoot tr:last-child td:last-child {
    border-bottom-right-radius: 12px !important;
  }

  .coach-report-preview__comparison-table th,
  .coach-report-preview__comparison-table td {
    font-size: 6.8pt !important;
    line-height: 1.4 !important;
    padding: 5px 8px !important;
    vertical-align: middle !important;
  }

  .coach-report-preview__comparison-table thead th,
  .coach-report-preview__comparison-table tfoot td {
    font-size: 7pt !important;
  }

  .coach-report-preview__data-table th,
  .coach-report-preview__data-table td {
    padding: 5px 7px !important;
  }

  /* Force desktop layout: graph left, grid placement right — same as modal. */
  .coach-report-preview__wise-layout {
    align-items: stretch !important;
    box-sizing: border-box !important;
    display: grid !important;
    gap: 16px !important;
    grid-template-columns: minmax(0, 1.55fr) minmax(0, 1fr) !important;
    max-width: 100% !important;
    overflow: visible !important;
    width: 100% !important;
  }

  .coach-report-preview__wise-grid-column,
  .coach-report-preview__wise-side-column {
    box-sizing: border-box !important;
    display: flex !important;
    max-width: 100% !important;
    min-width: 0 !important;
    overflow: hidden !important;
  }

  .coach-report-preview__leader-panel--chart {
    align-items: center !important;
    box-sizing: border-box !important;
    display: flex !important;
    justify-content: center !important;
    max-width: 100% !important;
    overflow: hidden !important;
    padding: 8px !important;
    width: 100% !important;
  }

  .coach-report-preview__grid-placement-card {
    box-sizing: border-box !important;
    display: flex !important;
    flex-direction: column !important;
    max-width: 100% !important;
    min-height: 0 !important;
    overflow: hidden !important;
    padding: 12px 12px 10px !important;
    width: 100% !important;
  }

  .coach-report-preview__grid-placement-label {
    font-size: 7pt !important;
  }

  .coach-report-preview__grid-metric-row {
    gap: 6px !important;
    margin-bottom: 8px !important;
  }

  .coach-report-preview__grid-metric-name {
    font-size: 8pt !important;
    overflow-wrap: break-word !important;
  }

  .coach-report-preview__grid-metric-value {
    font-size: 9pt !important;
  }

  .coach-report-preview__grid-quadrant-box {
    box-sizing: border-box !important;
    margin-top: 8px !important;
    max-width: 100% !important;
    overflow: hidden !important;
    padding: 10px 12px !important;
    width: 100% !important;
  }

  .coach-report-preview__grid-quadrant-title,
  .coach-report-preview__grid-quadrant-body,
  .coach-report-preview__grid-placement-text {
    color: #0f2c4c !important;
    font-family: system-ui, -apple-system, "Segoe UI", sans-serif !important;
    letter-spacing: normal !important;
    max-width: 100% !important;
    overflow: visible !important;
    overflow-wrap: break-word !important;
    text-rendering: geometricPrecision !important;
    white-space: normal !important;
    word-break: break-word !important;
    word-spacing: normal !important;
  }

  .coach-report-preview__grid-quadrant-title {
    font-size: 7.5pt !important;
    font-weight: 700 !important;
    line-height: 1.3 !important;
  }

  .coach-report-preview__grid-quadrant-body {
    color: #334155 !important;
    font-size: 7pt !important;
    font-weight: 400 !important;
    line-height: 1.4 !important;
  }

  .coach-report-preview__grid-placement-text {
    font-size: 7pt !important;
  }

  .coach-report-preview__leader-grid {
    margin: 0 auto !important;
    max-width: 100% !important;
    width: 100% !important;
  }

  .coach-report-preview__leader-grid .wlg-chart-wrap {
    aspect-ratio: 1 / 1 !important;
    break-inside: avoid !important;
    height: auto !important;
    max-height: none !important;
    max-width: 100% !important;
    page-break-inside: avoid !important;
    position: relative !important;
    width: 100% !important;
  }

  .coach-report-preview__leader-panel--chart {
    break-inside: avoid !important;
    page-break-inside: avoid !important;
  }

  .coach-report-preview__leader-grid .wlg-chart-wrap svg {
    display: block !important;
    height: 100% !important;
    left: 0 !important;
    max-width: 100% !important;
    position: absolute !important;
    top: 0 !important;
    width: 100% !important;
  }

  .coach-report-preview__admin-comments-card,
  .coach-report-preview__admin-comments-card--fill {
    background: #f8fafc !important;
    border: 1px solid #e2e8f0 !important;
    border-left: 4px solid var(--coach-report-accent) !important;
    border-radius: 10px !important;
    box-sizing: border-box !important;
    display: block !important;
    flex: 0 0 auto !important;
    margin-top: 14px !important;
    max-width: 100% !important;
    min-height: 0 !important;
    min-width: 0 !important;
    overflow: hidden !important;
    padding: 12px 16px 12px 16px !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    width: 100% !important;
  }

  .coach-report-preview__admin-comments-card::before {
    content: none !important;
    display: none !important;
  }

  .coach-report-preview__admin-comments-body-wrap {
    box-sizing: border-box !important;
    display: block !important;
    flex: 0 0 auto !important;
    max-width: 100% !important;
    min-height: 0 !important;
    min-width: 0 !important;
    width: 100% !important;
  }

  .coach-report-preview__admin-comments-body,
  .coach-report-preview__admin-comments-signature,
  .coach-report-preview__admin-comments-body.coach-report-preview__print-text,
  .coach-report-preview__admin-comments-signature.coach-report-preview__print-text,
  .coach-report-preview__admin-comments-body-wrap .coach-report-preview__print-text {
    box-sizing: border-box !important;
    flex: 0 0 auto !important;
    height: auto !important;
    max-height: none !important;
    max-width: 100% !important;
    min-height: 0 !important;
    min-width: 0 !important;
    overflow: hidden !important;
    overflow-wrap: break-word !important;
    width: 100% !important;
    word-break: break-word !important;
  }
}

@page {
  size: Letter;
  /* Bottom margin keeps content clear of the footer margin boxes. */
  margin: 0.15in 0.15in 0.32in 0.15in;

  @bottom-right {
    border-top: 1px solid #e2e8f0;
    color: #64748b;
    content: "Page " counter(page) " of " counter(pages);
    font-family: Arial, Helvetica, sans-serif;
    font-size: 7pt;
    font-weight: 600;
    padding-top: 4px;
    vertical-align: top;
  }
}

`;

export default reportDesignCss;



// WEBPACK FOOTER //
// ./src/report-design.ts